/**
 * Thin react-hook-form ↔ HeroUI bridges.
 *
 * Replaces shadcn's <FormField> + <FormItem> + <FormLabel> + <FormControl>
 * + <FormMessage> wrapper stack. HeroUI inputs accept `label`,
 * `description`, `errorMessage`, and `isInvalid` natively — so the bridge
 * is just: useController gives us field + fieldState, spread onto a
 * HeroUI primitive.
 *
 * Usage:
 *   const form = useForm({ resolver: zodResolver(schema) });
 *
 *   <HeroInputField control={form.control} name="email" label="Email" />
 *   <HeroSelectField control={form.control} name="role" label="Role">
 *     <SelectItem key="admin">Admin</SelectItem>
 *   </HeroSelectField>
 *   <HeroSwitchField control={form.control} name="optIn">Opt in</HeroSwitchField>
 *   <HeroTextareaField control={form.control} name="notes" label="Notes" />
 *   <HeroCheckboxField control={form.control} name="agree">I agree</HeroCheckboxField>
 *
 * For more complex fields (custom render, third-party widgets), use the
 * lower-level `HeroFormField` with a render prop.
 */

import {
  Checkbox,
  type CheckboxProps,
  Input,
  type InputProps,
  NumberInput,
  type NumberInputProps,
  Select,
  type SelectProps,
  Switch,
  type SwitchProps,
  Textarea,
  type TextAreaProps,
} from "@heroui/react";
import type { ReactNode } from "react";
import {
  type Control,
  type ControllerRenderProps,
  type FieldPath,
  type FieldValues,
  type ControllerFieldState,
  useController,
} from "react-hook-form";

interface BaseFieldProps<T extends FieldValues, N extends FieldPath<T>> {
  control: Control<T>;
  name: N;
  rules?: Parameters<typeof useController<T, N>>[0]["rules"];
}

/**
 * Lower-level escape hatch — use for fields where you want to render
 * something custom (third-party widget, multi-input field, etc.).
 */
export function HeroFormField<T extends FieldValues, N extends FieldPath<T>>(
  props: BaseFieldProps<T, N> & {
    render: (args: {
      field: ControllerRenderProps<T, N>;
      fieldState: ControllerFieldState;
    }) => ReactNode;
  }
) {
  const { field, fieldState } = useController({
    control: props.control,
    name: props.name,
    rules: props.rules,
  });
  return <>{props.render({ field, fieldState })}</>;
}

type InputFieldExtra = Omit<
  InputProps,
  "value" | "onChange" | "onBlur" | "name" | "errorMessage" | "isInvalid"
>;

export function HeroInputField<T extends FieldValues, N extends FieldPath<T>>({
  control,
  name,
  rules,
  ...rest
}: BaseFieldProps<T, N> & InputFieldExtra) {
  const { field, fieldState } = useController({ control, name, rules });
  return (
    <Input
      {...rest}
      name={field.name}
      value={(field.value as string | undefined) ?? ""}
      onValueChange={field.onChange}
      onBlur={field.onBlur}
      isInvalid={!!fieldState.error}
      errorMessage={fieldState.error?.message}
    />
  );
}

type NumberFieldExtra = Omit<
  NumberInputProps,
  "value" | "onChange" | "onBlur" | "name" | "errorMessage" | "isInvalid"
>;

export function HeroNumberField<T extends FieldValues, N extends FieldPath<T>>({
  control,
  name,
  rules,
  ...rest
}: BaseFieldProps<T, N> & NumberFieldExtra) {
  const { field, fieldState } = useController({ control, name, rules });
  return (
    <NumberInput
      {...rest}
      name={field.name}
      value={(field.value as number | undefined) ?? NaN}
      onValueChange={(v) => field.onChange(Number.isNaN(v) ? undefined : v)}
      onBlur={field.onBlur}
      isInvalid={!!fieldState.error}
      errorMessage={fieldState.error?.message}
    />
  );
}

type TextareaFieldExtra = Omit<
  TextAreaProps,
  "value" | "onChange" | "onBlur" | "name" | "errorMessage" | "isInvalid"
>;

export function HeroTextareaField<
  T extends FieldValues,
  N extends FieldPath<T>,
>({
  control,
  name,
  rules,
  ...rest
}: BaseFieldProps<T, N> & TextareaFieldExtra) {
  const { field, fieldState } = useController({ control, name, rules });
  return (
    <Textarea
      {...rest}
      name={field.name}
      value={(field.value as string | undefined) ?? ""}
      onValueChange={field.onChange}
      onBlur={field.onBlur}
      isInvalid={!!fieldState.error}
      errorMessage={fieldState.error?.message}
    />
  );
}

type SelectFieldExtra = Omit<
  SelectProps,
  | "value"
  | "selectedKeys"
  | "onChange"
  | "onSelectionChange"
  | "onBlur"
  | "name"
  | "errorMessage"
  | "isInvalid"
>;

export function HeroSelectField<T extends FieldValues, N extends FieldPath<T>>({
  control,
  name,
  rules,
  ...rest
}: BaseFieldProps<T, N> & SelectFieldExtra) {
  const { field, fieldState } = useController({ control, name, rules });
  const value = (field.value as string | undefined) ?? "";
  return (
    <Select
      {...rest}
      name={field.name}
      selectedKeys={value ? [value] : []}
      onSelectionChange={(keys) => {
        const next = Array.from(keys)[0];
        field.onChange(next ?? "");
      }}
      onBlur={field.onBlur}
      isInvalid={!!fieldState.error}
      errorMessage={fieldState.error?.message}
    />
  );
}

type SwitchFieldExtra = Omit<
  SwitchProps,
  "isSelected" | "onValueChange" | "onBlur" | "name"
>;

export function HeroSwitchField<T extends FieldValues, N extends FieldPath<T>>({
  control,
  name,
  rules,
  children,
  ...rest
}: BaseFieldProps<T, N> & SwitchFieldExtra & { children?: ReactNode }) {
  const { field } = useController({ control, name, rules });
  return (
    <Switch
      {...rest}
      name={field.name}
      isSelected={!!field.value}
      onValueChange={field.onChange}
    >
      {children}
    </Switch>
  );
}

type CheckboxFieldExtra = Omit<
  CheckboxProps,
  "isSelected" | "onValueChange" | "onBlur" | "name"
>;

export function HeroCheckboxField<
  T extends FieldValues,
  N extends FieldPath<T>,
>({
  control,
  name,
  rules,
  children,
  ...rest
}: BaseFieldProps<T, N> & CheckboxFieldExtra & { children?: ReactNode }) {
  const { field } = useController({ control, name, rules });
  return (
    <Checkbox
      {...rest}
      name={field.name}
      isSelected={!!field.value}
      onValueChange={field.onChange}
    >
      {children}
    </Checkbox>
  );
}
